/**
 * Wall height profiles — sloped ceilings (ADR 0044).
 *
 * A room does not have to be one height. Where two walls of different heights
 * meet, the wall between them slopes so its ends match its neighbours; and a
 * slope can start partway along a wall ("level for the first 3 feet, then rise
 * to meet the wall on the right" — owner, 2026-07-29).
 *
 * A profile is an ordered list of `{atUnits, heightUnits}` points measured from
 * the wall's START, with the height between two points found by straight-line
 * interpolation. That representation covers every case with no special-casing:
 *
 *   flat            [{0, 96}, {L, 96}]
 *   full slope      [{0, 96}, {L, 120}]
 *   level then rise [{0, 96}, {36, 96}, {L, 120}]
 *
 * Flat is the DEGENERATE case, not the primary one, which is why nothing here
 * treats it specially — the drawing code follows the points either way.
 *
 * These functions are pure and know nothing about corners; the model owns the
 * rule that a corner's height is shared by the two walls meeting there.
 */

/** Two heights within this many units count as the same (float slack). */
const FLAT_TOL = 0.01;

/**
 * Builds a normalised profile for one wall.
 *
 * Interior breakpoints are clamped into the wall, sorted, and de-duplicated —
 * a designer typing a distance longer than the wall, or two points at the same
 * spot, must not produce a profile that draws backwards. The end points always
 * exist, so `heightAt` can never fall off either end.
 *
 * @param {object} spec
 * @param {number} spec.lengthUnits the wall's length
 * @param {number} spec.startHeightUnits height at the wall's start corner
 * @param {number} spec.endHeightUnits height at the wall's end corner
 * @param {Array<{atUnits:number, heightUnits:number}>} [spec.breakpoints] interior points
 * @returns {Array<{atUnits:number, heightUnits:number}>} at least two points
 */
export function buildWallHeightProfile(spec = {}) {
  const length = Number(spec.lengthUnits) > 0 ? Number(spec.lengthUnits) : 0;
  const startH = Number(spec.startHeightUnits) > 0 ? Number(spec.startHeightUnits) : 0;
  const endH = Number(spec.endHeightUnits) > 0 ? Number(spec.endHeightUnits) : startH;

  const interior = (Array.isArray(spec.breakpoints) ? spec.breakpoints : [])
    .map((b) => ({ atUnits: Number(b?.atUnits), heightUnits: Number(b?.heightUnits) }))
    .filter((b) => Number.isFinite(b.atUnits) && b.heightUnits > 0)
    // Strictly INSIDE the wall: a point at either end would fight the corner
    // height, which is the shared value and always wins.
    .filter((b) => b.atUnits > FLAT_TOL && b.atUnits < length - FLAT_TOL)
    .sort((a, b) => a.atUnits - b.atUnits);

  const points = [{ atUnits: 0, heightUnits: startH }];
  for (const b of interior) {
    const last = points[points.length - 1];
    if (Math.abs(b.atUnits - last.atUnits) <= FLAT_TOL) {
      last.heightUnits = b.heightUnits; // same spot: the later entry wins
      continue;
    }
    points.push(b);
  }
  points.push({ atUnits: length, heightUnits: endH });
  return points;
}

/**
 * The wall's height at a distance along it, interpolating between the points
 * either side. Off either end clamps to that end's height rather than
 * extrapolating — a fixture 2" past the end of a wall is a rounding artefact,
 * not a request for a taller ceiling.
 *
 * @param {Array<{atUnits:number, heightUnits:number}>} profile
 * @param {number} alongUnits
 * @returns {number}
 */
export function heightAt(profile, alongUnits) {
  const points = Array.isArray(profile) ? profile : [];
  if (!points.length) return 0;
  const at = Number(alongUnits);
  if (!Number.isFinite(at)) return points[0].heightUnits;
  if (at <= points[0].atUnits) return points[0].heightUnits;
  const last = points[points.length - 1];
  if (at >= last.atUnits) return last.heightUnits;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (at > b.atUnits) continue;
    const span = b.atUnits - a.atUnits;
    if (span <= 0) return b.heightUnits;
    const t = (at - a.atUnits) / span;
    return a.heightUnits + (b.heightUnits - a.heightUnits) * t;
  }
  return last.heightUnits;
}

/** The tallest point on a profile — what the drawing must be tall enough for. */
export function profileMaxHeight(profile) {
  const points = Array.isArray(profile) ? profile : [];
  return points.reduce((max, p) => (p.heightUnits > max ? p.heightUnits : max), 0);
}

/** The lowest point on a profile. */
export function profileMinHeight(profile) {
  const points = Array.isArray(profile) ? profile : [];
  if (!points.length) return 0;
  return points.reduce((min, p) => (p.heightUnits < min ? p.heightUnits : min), points[0].heightUnits);
}

/** True when every point on the profile is the same height. */
export function profileIsFlat(profile) {
  const points = Array.isArray(profile) ? profile : [];
  if (points.length < 2) return true;
  const first = points[0].heightUnits;
  return points.every((p) => Math.abs(p.heightUnits - first) <= FLAT_TOL);
}
